import React, { useState, useEffect } from 'react';
import { backupService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeInput,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Database,
  Plus,
  CheckCircle,
  RotateCcw,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function BackupsPage() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureAvailable, setFeatureAvailable] = useState(true);
  
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [confirmAction, setConfirmAction] = useState('');
  const [saving, setSaving] = useState(false);
  const [description, setDescription] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await backupService.list();
      if (Array.isArray(data)) {
        setBackups(data);
        setFeatureAvailable(true);
      } else {
        setBackups([]);
        setFeatureAvailable(data !== null);
      }
    } catch (err) {
      if (err.status === 404) {
        setFeatureAvailable(false);
        setBackups([]);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await backupService.create(description || 'Manuel yedekleme');
      if (result) {
        toast.success('Yedekleme başarıyla oluşturuldu');
        setCreateOpen(false);
        setDescription('');
        fetchData();
      } else {
        toast.info('Yedekleme ozelligi mevcut degil');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (backup) => {
    try {
      const result = await backupService.verify(backup.id);
      if (result) {
        toast.success(`Yedekleme doğrulandı: ${result.status === 'valid' ? 'Geçerli' : 'Geçersiz'}`);
      } else {
        toast.info('Doğrulama özelliği mevcut değil');
      }
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const openConfirmModal = (backup, action) => {
    setSelectedBackup(backup);
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      if (confirmAction === 'restore') {
        const result = await backupService.restore(selectedBackup.id);
        if (result) {
          toast.success('Yedekleme geri yüklendi');
        } else {
          toast.info('Geri yükleme özelliği mevcut değil');
        }
      } else if (confirmAction === 'delete') {
        const result = await backupService.delete(selectedBackup.id);
        if (result !== null) {
          toast.success('Yedekleme silindi');
        } else {
          toast.info('Silme ozelligi mevcut degil');
        }
      }
      setConfirmOpen(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <NativeBadge variant="success">Tamamlandi</NativeBadge>;
      case 'in_progress': return <NativeBadge variant="default">Devam Ediyor</NativeBadge>;
      case 'failed': return <NativeBadge variant="danger">Basarisiz</NativeBadge>;
      default: return <NativeBadge variant="secondary">{status}</NativeBadge>;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!featureAvailable) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="backups-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Yedeklemeler</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Sistem yedekleme yonetimi</p>
        </div>
        <NativeAlert variant="warning" icon={AlertTriangle}>
          Yedekleme ozelligi su an aktif degil. Bu ozellik gelecek guncellemelerde eklenecektir.
        </NativeAlert>
      </div>
    );
  }

  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <NativeButton variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="backups-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Yedeklemeler</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Sistem yedeklemelerini yonetin</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <NativeButton variant="outline" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Yenile
          </NativeButton>
          <NativeButton onClick={() => setCreateOpen(true)} data-testid="create-backup-button">
            <Plus style={{ width: '16px', height: '16px' }} />
            Yeni Yedekleme
          </NativeButton>
        </div>
      </div>

      {/* Backups Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Database}>Yedekleme Listesi ({backups.length})</NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          {backups.length === 0 ? (
            <EmptyState icon={Database} title="Yedekleme bulunamadı" description="Henüz yedekleme oluşturulmamış" />
          ) : (
            <NativeTable>
              <NativeTableHeader>
                <NativeTableHead>Tarih</NativeTableHead>
                <NativeTableHead>Aciklama</NativeTableHead>
                <NativeTableHead>Durum</NativeTableHead>
                <NativeTableHead>Boyut</NativeTableHead>
                <NativeTableHead align="right">Islemler</NativeTableHead>
              </NativeTableHeader>
              <NativeTableBody>
                {backups.map((backup) => (
                  <NativeTableRow key={backup.id} data-testid={`backup-row-${backup.id}`}>
                    <NativeTableCell style={{ whiteSpace: 'nowrap' }}>
                      {backup.timestamp ? new Date(backup.timestamp).toLocaleString('tr-TR') : '-'}
                    </NativeTableCell>
                    <NativeTableCell>{backup.description || '-'}</NativeTableCell>
                    <NativeTableCell>{getStatusBadge(backup.status)}</NativeTableCell>
                    <NativeTableCell>{backup.size_mb ? `${backup.size_mb} MB` : (backup.size_bytes ? `${(backup.size_bytes / 1024 / 1024).toFixed(2)} MB` : '-')}</NativeTableCell>
                    <NativeTableCell align="right">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <NativeButton variant="ghost" size="icon" onClick={() => handleVerify(backup)} title="Dogrula">
                          <CheckCircle style={{ width: '16px', height: '16px' }} />
                        </NativeButton>
                        <NativeButton variant="ghost" size="icon" onClick={() => openConfirmModal(backup, 'restore')} title="Geri Yukle">
                          <RotateCcw style={{ width: '16px', height: '16px' }} />
                        </NativeButton>
                        <NativeButton variant="ghost" size="icon" onClick={() => openConfirmModal(backup, 'delete')} title="Sil" style={{ color: '#dc2626' }}>
                          <Trash2 style={{ width: '16px', height: '16px' }} />
                        </NativeButton>
                      </div>
                    </NativeTableCell>
                  </NativeTableRow>
                ))}
              </NativeTableBody>
            </NativeTable>
          )}
        </NativeCardContent>
      </NativeCard>

      {/* Create Modal */}
      <NativeModal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Yeni Yedekleme" disabled={saving}>
        <form onSubmit={handleCreate}>
          <NativeInput
            label="Açıklama"
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Yedekleme açıklaması girin..."
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={() => setCreateOpen(false)}>İptal</NativeButton>
            <NativeButton type="submit" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} /> : 'Oluştur'}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Confirm Modal */}
      <NativeModal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title={confirmAction === 'restore' ? 'Yedeklemeyi Geri Yukle' : 'Yedeklemeyi Sil'} disabled={saving}>
        <div>
          <NativeAlert variant={confirmAction === 'delete' ? 'danger' : 'warning'} icon={AlertTriangle}>
            {confirmAction === 'restore' 
              ? 'Bu yedeklemeyi geri yuklemek istediginizden emin misiniz? Mevcut veriler uzerine yazilacak.'
              : 'Bu yedeklemeyi silmek istediginizden emin misiniz? Bu islem geri alinamaz.'}
          </NativeAlert>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={() => setConfirmOpen(false)}>Iptal</NativeButton>
            <NativeButton onClick={handleConfirm} disabled={saving} variant={confirmAction === 'delete' ? 'danger' : 'primary'}>
              {saving ? <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} /> : 'Onayla'}
            </NativeButton>
          </div>
        </div>
      </NativeModal>
    </div>
  );
}
